import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowDownLeft, ArrowLeft, ArrowUpRight } from "lucide-react";

import { getInventoryLogs } from "src/api/inventory";
import { useAuth } from "src/providers/AuthProvider";
import { BranchSelect, DataTable, PageHead, SearchField, type Column, type RowsPerPageOption } from "src/components/common";
import { getCustomizationValue, renderNumber } from "src/lib/utils";

interface InventoryLogRow {
	uuid?: string;
	id?: number | string;
	name?: string;
	qty?: number;
	type?: string;
	first_name?: string;
	last_name?: string;
	created_at?: string;
}

const formatDate = (value?: string) => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "PPP");
};

const InventoryLogs = () => {
	const { user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();

	const inventoryLabel = getCustomizationValue(user, "inventory_label", true) || "Inventory";

	const search = searchParams.get("search") ?? undefined;
	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);
	const entity = searchParams.get("entity") ?? "";

	const setParam = (key: string, value: string | null) => {
		const next = new URLSearchParams(searchParams);
		if (value === null || value === "0") next.delete(key);
		else next.set(key, value);
		setSearchParams(next);
	};

	const { data, isLoading } = useQuery({
		queryKey: ["inventory-logs", page, limit, search, entity],
		queryFn: () => getInventoryLogs<InventoryLogRow>({ page, limit: limit === "All" ? 9999 : limit, search, entity }),
	});

	const list = data?.data;
	const logs = list?.data ?? [];

	const columns: Column<InventoryLogRow>[] = [
		{ key: "name", header: "Product", primary: true, cell: (row) => row.name ?? "—" },
		{ key: "qty", header: "Qty", cell: (row) => renderNumber(row.qty ?? 0) },
		{
			key: "type",
			header: "Direction",
			cell: (row) => {
				const isIn = String(row.type).toLowerCase().includes("in");
				return (
					<span
						className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-font-semibold ${
							isIn ? "tw-text-teal-dark" : "tw-text-amber"
						}`}
					>
						{isIn ? <ArrowDownLeft className="tw-h-3.5 tw-w-3.5" /> : <ArrowUpRight className="tw-h-3.5 tw-w-3.5" />}
						<span className="tw-capitalize">{row.type ?? "—"}</span>
					</span>
				);
			},
		},
		{ key: "by", header: "By", cell: (row) => [row.first_name, row.last_name].filter(Boolean).join(" ") || "N/A" },
		{ key: "date", header: "Date", cell: (row) => formatDate(row.created_at) },
	];

	return (
		<div className="tw-space-y-1">
			<PageHead
				title={`${inventoryLabel} Logs`}
				subtitle="Every stock movement in and out of your branches."
				actions={
					<Link
						to="/inventory"
						className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-border tw-bg-white tw-px-3 tw-py-2 tw-text-[12.5px] tw-font-semibold tw-text-text tw-transition-colors hover:tw-bg-bg"
					>
						<ArrowLeft className="tw-h-4 tw-w-4" />
						Back to {inventoryLabel}
					</Link>
				}
			/>

			<div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3 tw-py-4">
				<SearchField placeholder="Search product name..." />
				<BranchSelect value={entity} onChange={(value) => setParam("entity", value)} allLabel="All stores" />
			</div>

			<DataTable
				data={logs}
				columns={columns}
				rowKey={(row, index) => row.uuid ?? row.id ?? index}
				loading={isLoading}
				totalRows={list?.total ?? logs.length}
				page={page}
				onPageChange={(next) => setParam("page", String(next))}
				rowsPerPage={limit}
				onRowsPerPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("limit", String(next));
					params.set("page", "1");
					setSearchParams(params);
				}}
				emptyTitle="No stock movements yet"
				emptyDescription="Stock added, sold or transferred between branches will be logged here."
			/>
		</div>
	);
};

export default InventoryLogs;
